/**
 * RipartizioneMensile — vista mese-per-mese dei costi fissi o variabili
 * con export PDF. Riutilizzata da CostiFissi.jsx e CostiVariabili.jsx.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { FileText, CalendarDays } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/**
 * variante: "fissi" | "variabili"
 */
export default function RipartizioneMensile({ variante }) {
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear);

  const endpoint = variante === "fissi"
    ? "/costi-fissi/ripartizione-mensile"
    : "/costi-variabili/ripartizione-mensile";
  const exportPath = variante === "fissi"
    ? "/api/export/costi-fissi/ripartizione-mensile/pdf"
    : "/api/export/costi-variabili/ripartizione-mensile/pdf";

  const { data, isLoading } = useQuery({
    queryKey: ["ripartizione", variante, anno],
    queryFn: () => api.get(endpoint, { params: { anno } }).then((r) => r.data),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`ripartizione-${variante}`}>
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-slate-500" />
          <div>
            <div className="overline">Ripartizione mensile</div>
            <div className="text-xs text-slate-500">
              {variante === "fissi"
                ? "Distribuzione delle voci ricorrenti nei 12 mesi (importo con IVA)."
                : "Aggregazione delle voci per mese di scadenza / pagamento."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={anno}
            onChange={(e) => setAnno(Number(e.target.value))}
            data-testid={`ripartizione-${variante}-anno`}
            className="border border-slate-300 rounded-sm px-2 py-1 text-sm bg-white"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <a
            href={`${REACT_APP_BACKEND_URL}${exportPath}?anno=${anno}`}
            target="_blank" rel="noreferrer"
            data-testid={`btn-export-ripartizione-${variante}`}
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm"
          >
            <FileText className="w-4 h-4 text-rose-700" /> Esporta PDF
          </a>
        </div>
      </div>

      {isLoading && <div className="p-6 text-center text-slate-400 text-sm">Caricamento…</div>}
      {!isLoading && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <tr>
                <th className="text-left p-2 sticky left-0 bg-slate-50 min-w-[180px]">
                  {variante === "fissi" ? "Voce" : "Mese"}
                </th>
                {variante === "fissi"
                  ? MESI.map((m) => (
                      <th key={m} className="text-right p-2 min-w-[64px]">{m}</th>
                    ))
                  : [
                      <th key="prev" className="text-right p-2">Previsti</th>,
                      <th key="pag" className="text-right p-2">Pagati</th>,
                      <th key="tot" className="text-right p-2">Totale</th>,
                    ]}
                <th className="text-right p-2 min-w-[80px]">
                  {variante === "fissi" ? "Anno" : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {variante === "fissi" && (
                <>
                  {data.voci.map((v) => (
                    <tr key={v.costo_id} className="border-t border-slate-100">
                      <td className="p-2 sticky left-0 bg-white">
                        <div className="font-semibold">{v.descrizione}</div>
                        <div className="text-[10px] text-slate-500">{v.categoria} · {v.periodicita}</div>
                      </td>
                      {v.importi_mensili.map((m, idx) => (
                        <td key={idx} className={`text-right p-2 ${m > 0 ? "text-slate-900" : "text-slate-300"}`}>
                          {m > 0 ? eur(m) : "—"}
                        </td>
                      ))}
                      <td className="text-right p-2 font-bold">{eur(v.totale_annuo)}</td>
                    </tr>
                  ))}
                  {data.voci.length === 0 && (
                    <tr>
                      <td colSpan={14} className="p-8 text-center text-slate-400 italic">
                        Nessuna voce attiva in {anno}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-slate-300 bg-amber-50 font-bold">
                    <td className="p-2 sticky left-0 bg-amber-50">TOTALE</td>
                    {data.mesi.map((m) => (
                      <td key={m.mese} className="text-right p-2">{eur(m.totale)}</td>
                    ))}
                    <td className="text-right p-2">{eur(data.totale_annuo)}</td>
                  </tr>
                </>
              )}
              {variante === "variabili" && (
                <>
                  {data.mesi.map((m) => (
                    <tr key={m.mese} className="border-t border-slate-100">
                      <td className="p-2 font-semibold">{MESI[m.mese - 1]}</td>
                      <td className={`text-right p-2 ${m.previsto > 0 ? "text-rose-700" : "text-slate-300"}`}>
                        {m.previsto > 0 ? eur(m.previsto) : "—"}
                      </td>
                      <td className={`text-right p-2 ${m.pagato > 0 ? "text-slate-900" : "text-slate-300"}`}>
                        {m.pagato > 0 ? eur(m.pagato) : "—"}
                      </td>
                      <td className="text-right p-2 font-bold">{eur(m.totale)}</td>
                      <td></td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-amber-50 font-bold">
                    <td className="p-2">ANNO {data.anno}</td>
                    <td className="text-right p-2 text-rose-700">{eur(data.totale_previsto)}</td>
                    <td className="text-right p-2">{eur(data.totale_pagato)}</td>
                    <td className="text-right p-2">{eur(data.totale_annuo)}</td>
                    <td></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
